
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    getCourses,
    getUnitsByCourse,
    getOasByUnit,
    savePlanning,
    deleteUnit0 as deleteUnit0API,
} from '../api';

const CurriculumContext = createContext();

export const useCurriculum = () => useContext(CurriculumContext);

export const CurriculumProvider = ({ children }) => {
    const [courses, setCourses] = useState([]);
    const [units, setUnits] = useState([]);
    const [oas, setOas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingContent, setLoadingContent] = useState(false); // Estado para carga de contenido del curso
    const [selectedCourse, setSelectedCourse] = useState(null);
    // Selección de asignatura/curso/sección centralizada (docs/oa_modif.md §4.4):
    // compartida entre "Mis Clases" y "Planificación" para no pedirla dos veces.
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedCourseSection, setSelectedCourseSection] = useState('');
    const [selectionComplete, setSelectionComplete] = useState(false);
    // hasUnit0 se calcula dinámicamente basándose en si existe una unidad con short_name === 'UN0'
    const hasUnit0 = useMemo(() => {
        return units.some(unit => unit.short_name === 'UN0');
    }, [units]);
    const [unit0OAs, setUnit0OAs] = useState([]);
    const unit0CounterRef = useRef(0); // useRef para contador (sincrónico, no causa re-renders)
    const [shouldDeleteUnit0, setShouldDeleteUnit0] = useState(false); // Flag para eliminar Unidad 0 del backend

    // Cambios sin guardar en la matriz OA x Clase de "Planificación" (docs/new_bulk_ia.md
    // §2.1.1). OAClassMatrix.js reporta su estado y registra su propio handler de guardado
    // aquí, para que GlobalSidebar.js pueda avisar/guardar al navegar fuera de esa pantalla.
    const [matrixHasUnsavedChanges, setMatrixHasUnsavedChanges] = useState(false);
    const matrixSaveHandlerRef = useRef(null);

    const registerMatrixSaveHandler = useCallback((handler) => {
        matrixSaveHandlerRef.current = handler;
    }, []);

    const triggerMatrixSave = useCallback(async () => {
        if (matrixSaveHandlerRef.current) {
            await matrixSaveHandlerRef.current();
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log('Iniciando carga de datos del currículum...');
                const coursesData = await getCourses();
                console.log('Datos de cursos recibidos:', coursesData);
                
                // Asegurar que coursesData sea un array
                const safeCoursesData = Array.isArray(coursesData) ? coursesData : [];
                setCourses(safeCoursesData);

                console.log('Cursos cargados:', safeCoursesData.length, 'elementos');
                setLoading(false);
            } catch (error) {
                console.error('Error fetching curriculum data:', error);
                // Establecer arrays vacíos en caso de error
                setCourses([]);
                setUnits([]);
                setOas([]);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleCourseSelection = useCallback(async (courseId, sectionId = null) => {
        console.log('[CurriculumContext] 📞 handleCourseSelection called with:', courseId, 'section:', sectionId);

        if (!courseId) {
            console.log('[CurriculumContext] Clearing selection');
            setSelectedCourse(null);
            setUnits([]);
            setOas([]);
            setUnit0OAs([]);
            unit0CounterRef.current = 0; // Resetear contador
            setShouldDeleteUnit0(false); // Resetear flag de eliminación
            setLoadingContent(false);
            return;
        }

        console.log('[CurriculumContext] Setting selectedCourse to:', courseId);
        setSelectedCourse(courseId);
        // Activar loadingContent mientras se cargan todos los datos del curso
        setLoadingContent(true);

        try {
            console.log('[CurriculumContext] 📥 Fetching units for course:', courseId, 'section:', sectionId);
            const unitsData = await getUnitsByCourse(courseId, sectionId);
            const safeUnitsData = Array.isArray(unitsData) ? unitsData : [];
            console.log('[CurriculumContext] ✅ Units loaded:', safeUnitsData.length);

            // Cargar OAs de todas las unidades en paralelo, con fechas por sección si está disponible
            let flatOasData = [];
            if (safeUnitsData.length > 0) {
                console.log('[CurriculumContext] 📥 Fetching OAs for units (section:', sectionId, ')...');
                const oasData = await Promise.all(
                    safeUnitsData.map(unit => getOasByUnit(unit.organization_id, sectionId))
                );
                flatOasData = oasData.flat();
                console.log('[CurriculumContext] ✅ OAs loaded:', flatOasData.length);
            }

            // Actualizar todos los estados al mismo tiempo
            setUnits(safeUnitsData);
            setOas(flatOasData);
        } catch (error) {
            console.error('[CurriculumContext] ❌ Error loading course data:', error);
            setUnits([]);
            setOas([]);
        } finally {
            console.log('[CurriculumContext] ✅ Loading complete - mostrando contenido');
            // Desactivar loadingContent solo cuando todo esté cargado
            setLoadingContent(false);
        }
    }, []);

    const createUnit0 = useCallback(() => {
        if (hasUnit0) return;
        
        const unit0 = {
            organization_id: 0,
            name: "Unidad 0",
            description: "Unidad personalizada",
            short_name: "UN0",
            ref_organization_type_id: 22,
            region_geojson: null,
            subject_id: null,
            course_id: selectedCourse,
            available_carnegie_unit_credit: null,
            ref_course_section_delivery_mode_id: null,
            ref_single_sex_class_status_id: null,
            time_required_for_completion: null,
            ref_additional_credit_type_id: null,
            ref_instruction_language_id: null,
            virtual_indicator: null,
            organization_calendar_session_id: 0,
            ref_credit_type_earned_id: null,
            ref_advanced_placement_course_code_id: null,
            maximum_capacity: null,
            related_competency_framework_items: null
        };

        setUnits(prev => [unit0, ...prev]);
        setUnit0OAs([]);
        setShouldDeleteUnit0(false); // Resetear flag al crear nueva Unidad 0
    }, [hasUnit0, selectedCourse]);

    const deleteUnit0 = useCallback(() => {
        // Marcar que debe eliminarse la Unidad 0 del backend
        setShouldDeleteUnit0(true);
        
        // Eliminar Unidad 0 del estado local
        setUnits(prev => prev.filter(unit => unit.short_name !== 'UN0'));
        setUnit0OAs([]);
        unit0CounterRef.current = 0; // Resetear contador
    }, []);

    // Refleja en el estado local las fechas de una unidad ya persistidas en BD
    // como override de la sección seleccionada (coursesectionoadate), para
    // que sigan cargadas si el acordeón se vuelve a montar sin recargar.
    const applyUnitValidDates = useCallback((unitId, validStartDate, validEndDate) => {
        setUnits(prev => prev.map(u =>
            String(u.organization_id) === String(unitId)
                ? { ...u, valid_start_date: validStartDate || null, valid_end_date: validEndDate || null }
                : u
        ));
    }, []);

    const addOAToUnit0 = useCallback((oa) => {
        // Incrementar contador sincrónicamente (useRef no causa re-render)
        unit0CounterRef.current += 1;
        
        // Generar ID único
        const newId = `unit0_${unit0CounterRef.current}_${Date.now()}`;
        console.log('[CurriculumContext] Adding OA to Unit0 with new ID:', newId, 'Original ID:', oa.competency_definition_id);
        
        // Crear nuevo OA con ID único
        const newOA = {
            ...oa,
            competency_definition_id: newId,
            original_competency_definition_id: oa.competency_definition_id, // Guardar ID original
            unit_id: 0
        };
        
        // Actualizar estado (una sola llamada a setState)
        setUnit0OAs(prevOAs => {
            const updatedOAs = [...prevOAs, newOA];
            console.log('[CurriculumContext] Unit0 OAs after adding:', updatedOAs.map(o => ({ id: o.competency_definition_id, identifier: o.identifier })));
            return updatedOAs;
        });
    }, []);

    const updateOAInUnit0 = useCallback((oaId, updatedOA) => {
        setUnit0OAs(prev => 
            prev.map(oa => 
                oa.competency_definition_id === oaId 
                    ? { ...oa, ...updatedOA }
                    : oa
            )
        );
    }, []);

    const deleteOAFromUnit0 = useCallback((oaId) => {
        setUnit0OAs(prev => prev.filter(oa => oa.competency_definition_id !== oaId));
    }, []);

    // Selección de asignatura/curso/sección compartida entre "Mis Clases" y
    // "Planificación" (docs/oa_modif.md §4.4).
    const handleSelectionComplete = useCallback((selection) => {
        setSelectedSubject(selection.subject);
        setSelectedCourseSection(selection.courseSection);
        setSelectionComplete(true);
        handleCourseSelection(selection.course, selection.courseSection);
    }, [handleCourseSelection]);

    const resetSelection = useCallback(() => {
        setSelectedSubject('');
        setSelectedCourseSection('');
        setSelectionComplete(false);
        handleCourseSelection(null);
    }, [handleCourseSelection]);

    // NO incluir las funciones en las dependencias del useMemo
    // Las funciones ya están memoizadas con useCallback
    const value = useMemo(() => ({
        courses,
        units,
        oas,
        loading,
        loadingContent,
        selectedCourse,
        selectedSubject,
        selectedCourseSection,
        selectionComplete,
        hasUnit0,
        unit0OAs,
        shouldDeleteUnit0,
        matrixHasUnsavedChanges,
        setMatrixHasUnsavedChanges,
        registerMatrixSaveHandler,
        triggerMatrixSave,
        handleCourseSelection,
        handleSelectionComplete,
        resetSelection,
        createUnit0,
        deleteUnit0,
        addOAToUnit0,
        updateOAInUnit0,
        deleteOAFromUnit0,
        applyUnitValidDates,
    }), [
        courses,
        units,
        oas,
        loading,
        loadingContent,
        selectedCourse,
        selectedSubject,
        selectedCourseSection,
        selectionComplete,
        hasUnit0,
        unit0OAs,
        shouldDeleteUnit0,
        matrixHasUnsavedChanges,
        handleSelectionComplete,
        resetSelection,
        registerMatrixSaveHandler,
        triggerMatrixSave,
        // NO incluir el resto de las funciones aquí - ya están memoizadas
    ]);

    return (
        <CurriculumContext.Provider value={value}>
            {children}
        </CurriculumContext.Provider>
    );
};
